import React, { useState, useEffect } from 'react';
import { supabase } from './lib/supabase';
import { TournamentHeader } from './components/TournamentHeader';
import { Lock } from 'lucide-react';
import { Navigation } from './components/Navigation';
import { MainContent } from './components/MainContent';
import { SetupErrorBoundary } from './components/ErrorBoundary';
import { Leaderboard } from './components/Leaderboard';
import { TournamentSelector } from './components/TournamentSelector';
import { ImportData } from './components/ImportData';
import { GroupScoring } from './components/GroupScoring';
import { MembersProfile } from './components/members/MembersProfile';
import { MatchPlaySetup } from './components/matchplay/MatchPlaySetup';
import { parseMembersData } from './lib/members';
import { MatchPlayScoring } from './components/matchplay/MatchPlayScoring';
import { RegistrationModule } from './components/registration';
import { ChatProvider } from './contexts/ChatContext';
import { TXCupProvider } from './contexts/TXCupContext';
import { PlayerDeletionProvider } from './contexts/PlayerDeletionContext';
import { PlayerLockProvider } from './contexts/PlayerLockContext';
import { completeRegistration } from './lib/payment/paymentHandler';
import { SponsorProvider } from './contexts/SponsorContext';
import { RegistrationProvider, useRegistration } from './contexts/RegistrationContext';
import { ChatOverlay } from './components/chat/ChatOverlay';
import { MatchPlayLeaderboard } from './components/matchplay/MatchPlayLeaderboard';
import { Player, Round, Score, Tournament } from './types';
import { MatchPlay } from './types/matchplay';
import { OGChatbot } from './components/OGChatbot';
import { courses as defaultCourses } from './data/courses';
import { MatchplayDatabase } from './lib/matchplay-v2/database';
import { MatchplayTournament } from './types/matchplay-v2';
import { 
  loadAllLocalData, 
  loadFromLocalStorage,
  saveTournaments, 
  savePlayers, 
  saveRounds, 
  saveMatches 
} from './lib/localStorage';
import { connectionManager } from './lib/supabase/connectionManager';
import { StorageProvider } from './contexts/StorageContext';
import { VersionCheck } from './components/VersionCheck';

// Get today's date in YYYY-MM-DD format
const formattedDate = new Date((new Date()).toISOString().split('T')[0] + 'T12:00:00Z')
  .toISOString()
  .split('T')[0];

// Add version check
const APP_VERSION = '1.0.17'; // Updated to force new deployment

// Cache busting - track build version for cache invalidation
const BUILD_TIME = import.meta.env.VITE_BUILD_TIME || Date.now().toString();
const BUILD_VERSION = import.meta.env.VITE_BUILD_VERSION || new Date().toISOString();

// 24-hour cache clearing function
const clearAppCache = async () => {
  try {
    console.log('Clearing app cache (24-hour timer)');
    
    // Clear service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
        console.log('Service worker unregistered for daily cache clear');
      }
    }
    
    // Clear caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        await caches.delete(cacheName);
        console.log('Cache deleted:', cacheName);
      }
    }
    
    // Clear app-specific localStorage (preserve user settings)
    const keysToPreserve = ['adminPassword', 'isAdmin', 'selectedTournament', 'lastCacheClear'];
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !keysToPreserve.includes(key)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log('Removed localStorage key:', key);
    });
    
    // Clear sessionStorage
    sessionStorage.clear();
    console.log('SessionStorage cleared');
    
  } catch (error) {
    console.error('Cache clearing failed:', error);
  }
};

// Check for 24-hour cache clear
const checkDailyCacheClear = () => {
  console.log('=== Daily Cache Clear Check ===');
  const lastCacheClear = localStorage.getItem('lastCacheClear');
  const now = Date.now();
  const twentyFourHours = 24 * 60 * 60 * 1000; // 1 minute for testing
  
  console.log('Last cache clear:', lastCacheClear);
  console.log('Current time:', now);
  console.log('Time difference:', lastCacheClear ? (now - parseInt(lastCacheClear)) : 'No previous clear');
  console.log('Timer threshold:', twentyFourHours);
  
  if (!lastCacheClear || (now - parseInt(lastCacheClear)) > twentyFourHours) {
    console.log('24 hours passed, clearing app cache');
    clearAppCache();
    localStorage.setItem('lastCacheClear', now.toString());
  } else {
    console.log('Cache clear not needed yet');
  }
  console.log('=== End Cache Clear Check ===');
};

// Force mobile cache busting
const forceMobileCacheBust = () => {
  // Add version to URL to force reload
  const url = new URL(window.location.href);
  const currentVersion = url.searchParams.get('v');
  
  if (currentVersion !== BUILD_TIME) {
    url.searchParams.set('v', BUILD_TIME);
    url.searchParams.set('t', Date.now().toString());
    window.location.href = url.toString();
    return true; // Indicates we're reloading
  }
  return false;
};

// Mobile cache busting function
const clearMobileCache = async () => {
  try {
    // Clear service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
        console.log('Service worker unregistered for cache busting');
      }
    }
    
    // Clear caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        await caches.delete(cacheName);
        console.log('Cache deleted:', cacheName);
      }
    }
    
    // Force reload with version parameter
    if (!forceMobileCacheBust()) {
      // If we didn't reload, force a hard refresh
      window.location.reload(true);
    }
  } catch (error) {
    console.error('Cache clearing failed:', error);
    // Fallback: force reload anyway
    window.location.reload(true);
  }
};

  // Generate a proper UUID for the initial tournament
  const generateUUID = () => {
    // Try to use crypto.randomUUID() if available
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    
    // Fallback to a custom UUID generation
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Helper function to convert MatchplayTournament to Tournament format
  const convertMatchplayTournament = (mpTournament: MatchplayTournament): Tournament => {
    return {
      id: mpTournament.id,
      name: mpTournament.name,
      startDate: mpTournament.date,
      endDate: mpTournament.date,
      courseId: defaultCourses[0].id, // Default course for now
      selectedTee: defaultCourses[0].tees[0].name, // Default tee
      format: 'match' as const,
      status: mpTournament.status === 'completed' ? 'completed' : 
              mpTournament.status === 'active' ? 'active' : 'registration',
      scoringType: 'gross' as const,
      tournamentType: 'matchplay-v2' as const,
      metadata: {
        description: `${mpTournament.format} Matchplay Tournament`,
        organizer: 'HVGA',
        contactEmail: '',
        website: '',
        sponsors: [],
        notes: ''
      }
    };
  };

  export default function App() {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [activeTab, setActiveTab] = useState<'tournaments' | 'scoring' | 'leaderboard' | 'import' | 'matchplay' | 'matchplay-v2' | 'members' | 'tx-cup'>('tournaments');
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [adminPassword, setAdminPassword] = useState('admin123');
  const [showAdminPrompt, setShowAdminPrompt] = useState(false);
  const [adminPasswordInput, setAdminPasswordInput] = useState('');
  const [adminError, setAdminError] = useState('');
  const [matches, setMatches] = useState<MatchPlay[]>([]);
  const [activeMatch, setActiveMatch] = useState<string | undefined>();
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [selectedPlayers, setSelectedPlayers] = useState<Player[]>([]);
  const [scores, setScores] = useState<Record<string, Record<number, number>>>({});
  const [initError, setInitError] = useState<string | null>(null);
  const [currentHoleIndex, setCurrentHoleIndex] = useState(0);
  const [isSelectingPlayers, setIsSelectingPlayers] = useState<boolean>(true);
  const [completedHoles, setCompletedHoles] = useState<number[]>([]);
  const [isOffline, setIsOffline] = useState<boolean>(!navigator.onLine);
  const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false);
  const [tournamentSubscription, setTournamentSubscription] = useState<ReturnType<typeof supabase.channel> | null>(null);
  const [membersData, setMembersData] = useState<MemberProfile[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('2025');

  // Cache clearing - run when component mounts
  useEffect(() => {
    console.log('=== Cache clearing code reached ===');
    if (window.location.hostname === 'hvga.vercel.app' || window.location.hostname === 'localhost') {
      console.log('=== Production environment detected ===');
      // Check for daily cache clear first
      checkDailyCacheClear();
    }
  }, []); // Empty dependency array means this runs once when component mounts

  // Load members data from CSV
  useEffect(() => {
    fetch('/members.csv')
      .then(response => response.text())
      .then(data => {
        const members = parseMembersData(data);
        setMembersData(members);
      })
      .catch(error => console.error('Failed to load members data:', error));
  }, []);
  
  const handleDeleteTournament = async (tournamentToDelete: Tournament) => {
    if (!isAdmin) return;

    const confirmMessage = 'Are you sure you want to delete this tournament? ' + 
      'This will permanently delete all associated players, matches, and messages. ' + 
      'This action cannot be undone.';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const supabase = connectionManager.getClient();
      if (!supabase) {
        throw new Error('Database connection not available');
      }

      // Remove tournament and related data from local state first
      const updatedTournaments = tournaments.filter(t => t.id !== tournamentToDelete.id);
      const updatedPlayers = players.filter(p => p.tournamentId !== tournamentToDelete.id);
      const updatedMatches = matches.filter(m => 
        !m.team1.players.some(p => p.tournamentId === tournamentToDelete.id) &&
        !m.team2.players.some(p => p.tournamentId === tournamentToDelete.id)
      );

      // If the deleted tournament was selected, clear selection
      if (selectedTournament?.id === tournamentToDelete.id) {
        setSelectedTournament(null);
        setActiveTab('tournaments');
      }

      // First delete related records
      await Promise.all([
        supabase.from('players').delete().eq('tournament_id', tournamentToDelete.id),
        supabase.from('matches').delete().eq('tournament_id', tournamentToDelete.id),
        supabase.from('messages').delete().eq('tournament_id', tournamentToDelete.id)
      ]);

      // Delete from Supabase
      const { error } = await supabase
        .from('tournaments')
        .delete()
        .eq('id', tournamentToDelete.id);

      if (error) throw error;

      // Update local state after successful delete
      setTournaments(updatedTournaments);
      setPlayers(updatedPlayers);
      setMatches(updatedMatches);

    } catch (error) {
      console.error('Failed to delete tournament:', error);
      
      // Restore state if delete fails
      const localData = loadAllLocalData();
      setTournaments(localData.tournaments);
      setPlayers(localData.players);
      setMatches(localData.matches);
      
      // Show error to user
      alert(
        error instanceof Error 
          ? `Failed to delete tournament: ${error.message}`
          : 'Failed to delete tournament. Please try again.'
      );
    }
  };

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      // Check for pending changes
      const pendingSyncs = loadFromLocalStorage('hvga_pending_sync');
      setHasPendingChanges(!!pendingSyncs?.length);
    };
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Initialize data and setup Supabase subscriptions
  useEffect(() => {
    const init = async () => {
      // Enable debug logging for payment process
      console.log('Initializing app with payment debug logging enabled');
      
      // Always start with loading state
      setIsInitializing(true);
      
      // Subscribe to connection state changes
      const unsubscribe = connectionManager.subscribe((connected) => {
        setIsConnected(connected);
        if (!connected && !import.meta.env.VITE_SUPABASE_URL) {
          setInitError('Missing Supabase configuration. Please check environment variables.');
        } else {
          setInitError(null);
        }
      });
      
      try {
        // Check if Supabase needs to be configured
        if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
          if (tournamentSubscription) {
            tournamentSubscription.unsubscribe();
            setTournamentSubscription(null);
          }
          setInitError('Missing Supabase configuration. Please check environment variables.');
          setIsConnected(false);
          const localData = loadAllLocalData();
          setTournaments(localData.tournaments);
          setPlayers(localData.players);
          setMatches(localData.matches);
          return; 
        }

        const supabase = connectionManager.getClient();
        if (supabase && connectionManager.isConnected()) {
          // Set up real-time subscription for tournament changes
          const subscription = supabase
            .channel('tournament-changes')
            .on(
              'postgres_changes',
              { event: '*', schema: 'public', table: 'tournaments' },
              async (payload) => {
                try {
                  // Handle tournament updates
                  if (payload.eventType === 'UPDATE') {
                    const updatedTournament = {
                      id: payload.new.id,
                      name: payload.new.name,
                      startDate: payload.new.start_date,
                      endDate: payload.new.end_date,
                      courseId: payload.new.course_id,
                      selectedTee: payload.new.selected_tee,
                      format: payload.new.format,
                      status: payload.new.status,
                      scoringType: payload.new.scoring_type,
                      metadata: payload.new.metadata,
                      playerLimit: payload.new.player_limit,
                      registrationDeadline: payload.new.registration_deadline,
                      rules: payload.new.rules
                    };

                    // Update tournaments list
                    setTournaments(prev => 
                      prev.map(t => t.id === updatedTournament.id ? updatedTournament : t)
                    );

                    // Update selected tournament if it's the one that changed
                    if (selectedTournament?.id === updatedTournament.id) {
                      setSelectedTournament(updatedTournament);
                      
                      // Update active tab based on tournament status
                      if (updatedTournament.status === 'completed') {
                        setActiveTab('leaderboard');
                      } else if (updatedTournament.status === 'active') {
                        setActiveTab(updatedTournament.format === 'match' ? 'matchplay' : 'scoring');
                      }
                    }
                  }
                } catch (error) {
                  console.error('Error processing tournament update:', error);
                }
              }
            )
            .subscribe();

          setTournamentSubscription(subscription);

          // First fetch tournaments
          const { data: supabaseTournaments, error: tournamentsError } = await supabase
            .from('tournaments')
            .select(`
              id,
              name,
              start_date,
              end_date,
              course_id,
              selected_tee,
              format,
              status,
              scoring_type,
              metadata,
              player_limit,
              registration_deadline,
              rules
            `)
            .order('created_at', { ascending: false });

          if (tournamentsError) {
            // Only log non-connection errors
            if (!tournamentsError.message?.includes('Failed to fetch')) {
              console.error('Error fetching tournaments:', tournamentsError);
            }
            setInitError(
              !navigator.onLine 
                ? 'Working in offline mode - changes will sync when connection is restored'
                : 'Click "Connect to Supabase" to set up your project'
            );
            setIsConnected(false);
            const localData = loadAllLocalData();
            setTournaments(localData.tournaments);
            setPlayers(localData.players);
            setMatches(localData.matches);
            return;
          } else if (supabaseTournaments) {
            // Format regular tournaments
            const formattedTournaments = supabaseTournaments.map(t => ({
              id: t.id,
              name: t.name,
              startDate: t.start_date || formattedDate,
              endDate: t.end_date || formattedDate,
              courseId: t.course_id || defaultCourses[0].id,
              selectedTee: t.selected_tee || defaultCourses[0].tees[0].name,
              format: t.format || 'stroke',
              status: t.status || 'draft',
              scoringType: t.scoring_type || 'gross',
              metadata: t.metadata || {
                description: '',
                organizer: '',
                contactEmail: '',
                website: '',
                sponsors: [],
                notes: ''
              },
              playerLimit: t.player_limit || 0,
              registrationDeadline: t.registration_deadline || '',
              rules: t.rules || '',
              tournamentType: 'regular' as const
            }));

            // Also fetch matchplay-v2 tournaments
            let loadedTournaments = formattedTournaments;
            try {
              const matchplayTournaments = await MatchplayDatabase.getTournaments();
              const formattedMatchplayTournaments = matchplayTournaments.map(convertMatchplayTournament);
              
              // Merge both types of tournaments
              const allTournaments = [...formattedTournaments, ...formattedMatchplayTournaments]
                .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
              
              loadedTournaments = allTournaments;
              setTournaments(allTournaments);
              saveTournaments(allTournaments);
              console.log('Loaded all tournaments:', {
                regular: formattedTournaments.length,
                matchplay: formattedMatchplayTournaments.length,
                total: allTournaments.length
              });
            } catch (mpError) {
              console.warn('Could not load matchplay tournaments:', mpError);
              // Just use regular tournaments if matchplay loading fails
              setTournaments(formattedTournaments);
              saveTournaments(formattedTournaments);
              console.log('Loaded tournaments (regular only):', formattedTournaments.length);
            }
            
            setIsConnected(true);
            
            // Select the first active tournament if none is selected
            if (!selectedTournament) {
              // Prioritize active tournaments over registration tournaments
              const activeTournament = loadedTournaments.find(t => t.status === 'active') || 
                                    loadedTournaments.find(t => t.status === 'registration');
              
              console.log('Available tournaments for auto-selection:', loadedTournaments.map(t => ({ 
                name: t.name, 
                status: t.status, 
                type: t.tournamentType || 'regular' 
              })));
              
              if (activeTournament) {
                setSelectedTournament(activeTournament);
                console.log('Auto-selected tournament:', activeTournament.name, 'Status:', activeTournament.status, 'Type:', activeTournament.tournamentType || 'regular');
                // Set appropriate tab based on tournament status
                if (activeTournament.status === 'registration') {
                  setActiveTab('registration');
                } else {
                  setActiveTab(activeTournament.format === 'match' ? 'matchplay' : 'scoring');
                }
              } else {
                console.log('No active tournaments found. Available tournaments:', loadedTournaments.map(t => ({ name: t.name, status: t.status })));
                // If there are tournaments but none active, default to leaderboard
                if (loadedTournaments.length > 0 && activeTab === 'scoring') {
                  setActiveTab('leaderboard');
                  console.log('Defaulting to leaderboard tab since no active tournament');
                }
              }
            }
          }
          
          // Then fetch players
          const { data: supabasePlayers, error: playersError } = await supabase
            .from('players')
            .select('*')
            .order('name');
            
          if (playersError) {
            console.error('Error fetching players:', playersError);
          } else if (supabasePlayers) {
            const formattedPlayers = supabasePlayers.map(p => ({
              id: p.id,
              name: p.name,
              tournamentId: p.tournament_id,
              division: p.division,
              handicap: p.handicap,
              flight: p.flight,
              team: p.team,
              matchNo: p.match_no,
              startingHole: p.starting_hole,
              notes: p.notes,
              scores: p.scores || [],
              totalScore: p.total_score || 0
            }));
            setPlayers(formattedPlayers);
            savePlayers(formattedPlayers);
          }
          // Fetch matches
          const { data: supabaseMatches, error: matchesError } = await supabase
            .from('matches')
            .select('*')
            .order('date');
            
          if (matchesError) {
            console.error('Error fetching matches:', matchesError);
          } else if (supabaseMatches) {
            setMatches(supabaseMatches);
            saveMatches(supabaseMatches);
          }
        }

      } catch (error) {
        console.error('Error during initialization:', error);
        setInitError(
          !navigator.onLine
            ? 'Working in offline mode - changes will sync when connection is restored'
            : 'Click "Connect to Supabase" to set up your project.'
        );
        setIsConnected(false);
        const localData = loadAllLocalData();
        setTournaments(localData.tournaments);
        setPlayers(localData.players);
        setMatches(localData.matches);
      } finally {
        setIsInitializing(false);
      }
    };

    init().catch(console.error);
    
    // Cleanup subscription on unmount
    return () => {
      if (tournamentSubscription) {
        tournamentSubscription.unsubscribe();
      }
    };
  }, []);

  const handleCreateTournament = () => {
    if (!isAdmin) return;

    const newTournament: Tournament = {
      id: generateUUID(),
      name: 'New Tournament',
      startDate: formattedDate,
      endDate: formattedDate,
      courseId: '',
      selectedTee: '',
      format: 'stroke',
      status: 'draft',
      scoringType: 'gross',
      metadata: {
        description: '',
        organizer: '',
        contactEmail: '',
        website: '',
        sponsors: [],
        notes: ''
      },
      playerLimit: 0,
      registrationDeadline: '',
      rules: ''
    };

    // Update local state first
    const updatedTournaments = [...tournaments, newTournament];
    setTournaments(updatedTournaments);
    setSelectedTournament(newTournament);

    // Clear any existing players for this tournament
    const filteredPlayers = players.filter(p => p.tournamentId !== newTournament.id);
    setPlayers(filteredPlayers);
    savePlayers(filteredPlayers);

    // Save tournaments
    saveTournaments(updatedTournaments).catch(error => {
      console.error('Failed to sync new tournament:', error);
    });
  };

  const handleTournamentUpdate = async (updatedTournament: Tournament) => {
    if (!isAdmin) return;
    
    if (!updatedTournament) {
      console.error('Invalid tournament data');
      return;
    }

    const startDate = new Date(updatedTournament.startDate);
    const endDate = new Date(updatedTournament.endDate);
    
    if (updatedTournament.status === 'completed' && endDate < startDate) {
      updatedTournament.endDate = new Date().toISOString().split('T')[0];
    }
    
    if (updatedTournament.status === 'completed') {
      localStorage.removeItem('hvga_scoring_state');
    }

    try {
      // Validate required fields
      if (!updatedTournament.id) {
        throw new Error('Tournament ID is required');
      }

      if (!updatedTournament.startDate || !updatedTournament.endDate) {
        throw new Error('Missing required fields: start date or end date');
      }

      // Ensure status is valid
      if (!['draft', 'active', 'registration', 'completed'].includes(updatedTournament.status)) {
        throw new Error('Invalid tournament status');
      }

      // Additional validation for activation
      if (updatedTournament.status === 'active') {
        const hasActiveTournament = tournaments.some(t => 
          t.id !== updatedTournament.id && t.status === 'active'
        );
        
        if (hasActiveTournament) {
          throw new Error('Another tournament is already active');
        }

        // Ensure tournament has required fields before activation
        if (!updatedTournament.courseId || !updatedTournament.selectedTee) {
          throw new Error('Please select a course and tee first');
        }

        // Ensure tournament has at least one player
        const tournamentPlayers = players.filter(p => p.tournamentId === updatedTournament.id);
        if (tournamentPlayers.length === 0) {
          throw new Error('Please add at least one player first');
        }
      }

      // Update local state first
      const updatedTournaments = tournaments.map(t =>
        t.id === updatedTournament.id ? updatedTournament : t
      );
      
      // Update local state
      setTournaments(updatedTournaments);
      setSelectedTournament(updatedTournament);
      
      const supabase = connectionManager.getClient();
      if (!supabase) {
        throw new Error('Database connection not available');
      }

      // TODO: Temporarily disabled direct tournament_costs update to avoid race condition with trigger
      // If costs are being updated, sync them to tournament_costs table
      /*
      if (updatedTournament.metadata?.cost) {
        const { error: costError } = await supabase
          .from('tournament_costs')
          .upsert({
            tournament_id: updatedTournament.id,
            member_cost: updatedTournament.metadata.cost.member.amount,
            non_member_cost: updatedTournament.metadata.cost.nonMember.amount,
            late_fee: updatedTournament.metadata.cost.lateRegistration.amount
          });

        if (costError) throw costError;
      }
      */

      // Update tournament directly
      const { error: tournamentError } = await supabase
        .from('tournaments')
        .update({
          name: updatedTournament.name,
          start_date: updatedTournament.startDate,
          end_date: updatedTournament.endDate,
          course_id: updatedTournament.courseId,
          selected_tee: updatedTournament.selectedTee,
          format: updatedTournament.format,
          status: updatedTournament.status,
          scoring_type: updatedTournament.scoringType,
          metadata: updatedTournament.metadata || {},
          player_limit: updatedTournament.playerLimit,
          registration_deadline: updatedTournament.registrationDeadline || null,
          rules: updatedTournament.rules || '',
          updated_at: new Date().toISOString()
        })
        .eq('id', updatedTournament.id);

      if (tournamentError) throw tournamentError;

      // Save tournaments which handles both localStorage and Supabase sync
      saveTournaments(updatedTournaments).catch(error => {
        console.warn('Tournament sync warning:', error);
        // Only show alert for validation errors, not sync issues
        if (error instanceof Error && 
            !error.message.includes('Failed to fetch') && 
            !error.message.includes('not configured')) {
          alert(`Failed to sync tournament: ${error.message}`);
        }
      });

      // Only change tab for specific status changes
      if (updatedTournament.status === 'active' && selectedTournament?.status !== 'active') {
        setActiveTab(updatedTournament.format === 'match' ? 'matchplay' : 'scoring');
      } else if (updatedTournament.status === 'completed' && selectedTournament?.status !== 'completed') {
        setActiveTab('leaderboard');
      }
    } catch (error) {
      console.error('Failed to update tournament:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to update tournament: ${errorMessage}`);
      
      // Only show alert for validation errors, not sync issues
      
      // Revert local state
      const revertedTournament = tournaments.find(t => t.id === updatedTournament.id);
      if (revertedTournament) {
        setSelectedTournament(revertedTournament);
      }
    }
  };

  const handlePlayersUpdate = (updatedPlayers: Player[]) => {
    if (!selectedTournament) {
      console.warn('Cannot update players: No tournament selected');
      return;
    }

    // Ensure tournament ID is set for all players
    const playersWithTournament = updatedPlayers.map(player => {
      if (!player.tournamentId) {
        console.log('Setting tournament ID for player:', player.name);
        return {
          ...player,
          tournamentId: selectedTournament.id,
          notes: player.notes || null,
        };
      }
      return player;
    });
    
    setPlayers(playersWithTournament);
    savePlayers(playersWithTournament);
  };

  const handleMatchesUpdate = (updatedMatches: MatchPlay[]) => {
    setMatches(updatedMatches);
    saveMatches(updatedMatches);
  };

  const handleScoreSubmit = (scores: Record<string, Score[]>) => {
    if (!selectedTournament) return;

    // Prevent score submission for completed tournaments
    if (selectedTournament.status === 'completed') {
      console.error('Cannot submit scores for completed tournament');
      alert('Cannot submit scores for a completed tournament');
      return;
    }

    const supabaseClient = connectionManager.getClient();

    const syncScores = async (updatedPlayers: Player[]) => {
      if (!supabaseClient || !navigator.onLine) return;

      try {
        // Process one player at a time to avoid unique constraint violations
        for (const player of updatedPlayers) {
          // Verify the tournament is still not completed before syncing
          const { data: tournament } = await supabaseClient
            .from('tournaments')
            .select('status')
            .eq('id', selectedTournament.id)
            .single();

          if (tournament?.status === 'completed') {
            console.error('Tournament was completed during score submission');
            throw new Error('Tournament was completed during score submission');
          }

          // Filter out invalid scores and ensure proper formatting
          const validScores = (player.scores || [])
            .filter(score => 
              score && 
              typeof score.hole === 'number' && 
              score.hole > 0 && 
              score.hole <= 18 &&
              typeof score.strokes === 'number' && 
              score.strokes > 0
            )
            .map(score => ({
              hole: score.hole,
              strokes: score.strokes,
              netStrokes: score.netStrokes || score.strokes,
              tournamentId: score.tournamentId,
              playerId: score.playerId,
              synced: score.synced || false
            }));

          const { error } = await supabaseClient
            .from('players')
            .upsert({
              id: player.id,
              name: player.name,
              tournament_id: player.tournamentId,
              handicap: player.handicap,
              flight: player.flight,
              team: player.team,
              match_no: player.matchNo,
              starting_hole: player.startingHole,
              scores: validScores,
              total_score: validScores.reduce((sum, score) => sum + score.strokes, 0)
            });

          if (error) throw error;
        }
      } catch (error) {
        console.error('Failed to sync scores:', error);
        throw error;
      }
    };

    try {
      // Update players with new scores
      const updatedPlayers = players.map(player => {
        const playerScores = scores[player.id];
        if (!playerScores) return player;

        // Get the current tournament ID from the first score
        const currentTournamentId = playerScores[0]?.tournamentId;
        if (!currentTournamentId) return player;

        // Filter out invalid scores and ensure proper formatting
        const validScores = playerScores
          .filter(score => 
            score && 
            typeof score.hole === 'number' && 
            score.hole > 0 && 
            score.hole <= 18 &&
            typeof score.strokes === 'number' && 
            score.strokes > 0
          )
          .map(score => ({
            hole: score.hole,
            strokes: score.strokes,
            netStrokes: score.netStrokes || score.strokes,
            tournamentId: score.tournamentId,
            playerId: score.playerId,
            synced: score.synced || false
          }));

        const totalScore = validScores.reduce((sum, score) => sum + score.strokes, 0);
        const existingScores = player.scores || [];
        
        // Filter out any existing scores for the current tournament
        const otherTournamentScores = existingScores.filter(s => s.tournamentId !== currentTournamentId);
        
        // Combine scores from other tournaments with the new scores
        const mergedScores = [...otherTournamentScores, ...validScores];

        return {
          ...player,
          scores: mergedScores,
          totalScore
        };
      });

      // Save players and reset scoring state
      handlePlayersUpdate(updatedPlayers);
      
      // Immediately sync with Supabase
      syncScores(updatedPlayers).catch(error => {
        console.error('Failed to sync scores:', error);
        alert('Failed to save scores. Please try again.');
      });
    } catch (error) {
      console.error('Failed to submit scores:', error);
      alert('Failed to submit scores. Please try again.');
    }
  };
  
  // Load scoring state from localStorage on mount
  useEffect(() => {
    // Check for payment success
    const urlParams = new URLSearchParams(window.location.search);
    console.log('Checking URL parameters:', {
      params: Object.fromEntries(urlParams),
      pathname: window.location.pathname,
      search: window.location.search
    });
    
    if (urlParams.get('payment') === 'success') {
      console.log('Payment success detected, completing registration');
      completeRegistration()
        .then(result => {
          console.log('Registration completion result:', result);
          if (result?.success) {
            // Clear URL params
            window.history.replaceState({}, '', window.location.pathname);
          }
        })
        .catch(error => {
          console.error('Failed to complete registration:', error);
        });
    }

    const storedScoringState = localStorage.getItem('hvga_scoring_state');
    if (storedScoringState) {
      try {
        const state = JSON.parse(storedScoringState);
        if (state.tournamentId === selectedTournament?.id) {
          setSelectedPlayers(state.selectedPlayers);
          setScores(state.scores);
          setCurrentHoleIndex(state.currentHoleIndex);
          setIsSelectingPlayers(state.isSelectingPlayers);
          setCompletedHoles(state.completedHoles);
        }
      } catch (error) {
        console.error('Error loading scoring state:', error);
      }
    }
  }, [selectedTournament]);

  // Save scoring state to localStorage whenever it changes
  useEffect(() => {
    if (selectedTournament && !isSelectingPlayers) {
      const scoringState = {
        tournamentId: selectedTournament.id,
        selectedPlayers,
        scores,
        currentHoleIndex,
        isSelectingPlayers,
        completedHoles
      };
      localStorage.setItem('hvga_scoring_state', JSON.stringify(scoringState));
    }
  }, [selectedTournament, selectedPlayers, scores, currentHoleIndex, isSelectingPlayers, completedHoles]);

  const handleResetScoring = () => {
    setSelectedPlayers([]);
    setScores({});
    setCurrentHoleIndex(0);
    setIsSelectingPlayers(true);
    setCompletedHoles([]);
    localStorage.removeItem('hvga_scoring_state');
  };

  const handleAdminLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (adminPasswordInput === adminPassword) {
      setIsAdmin(true);
      setShowAdminPrompt(false);
      setAdminPasswordInput('');
      setAdminError('');
    } else {
      setAdminError('Incorrect password');
    }
  };

  const handleAdminLogout = () => {
    setIsAdmin(false);
  };

  const handlePasswordChange = (oldPassword: string, newPassword: string) => {
    if (oldPassword === adminPassword) {
      setAdminPassword(newPassword);
    }
  };

  const currentCourse = selectedTournament 
    ? defaultCourses.find(c => c.id === selectedTournament.courseId)
    : null;

  const renderTabs = () => {
    const isScoringDisabled = selectedTournament?.status === 'completed' || 
      (selectedTournament?.players?.some(player => 
        player.id === currentUser?.id && 
        player.scores?.length === 18
      ));

    return (
      <div className="flex space-x-4 mb-4">
        <button
          onClick={() => setActiveTab('leaderboard')}
          className={`px-4 py-2 rounded ${
            activeTab === 'leaderboard'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Leaderboard
        </button>
        <button
          onClick={() => setActiveTab('scoring')}
          disabled={isScoringDisabled}
          className={`px-4 py-2 rounded ${
            activeTab === 'scoring'
              ? 'bg-blue-500 text-white'
              : isScoringDisabled
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Scoring
        </button>
      </div>
    );
  };

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-lg shadow-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
          {hasPendingChanges && !isOffline && (
            <div className="mt-4 text-amber-600 text-sm">
              Syncing pending changes...
            </div>
          )}
          {isOffline && (
            <div className="mt-4 text-amber-600 text-sm">
              Working offline - changes will sync when connection is restored
            </div>
          )}
          {initError && (
            <div className="mt-4 text-red-600 text-sm">
              {initError}
              <button
                onClick={() => window.location.reload()}
                className="ml-2 underline hover:text-red-700"
              >
                Refresh
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <VersionCheck />
      <StorageProvider>
        <ChatProvider>
          <TXCupProvider>
            <RegistrationProvider>
              {({ completeRegistration, cancelRegistration, isRegistering }) => (
              <PlayerLockProvider>
              <PlayerDeletionProvider
                onPlayersUpdate={handlePlayersUpdate}
                initialPlayers={players}
              >
                <SponsorProvider>
                <div className="min-h-screen bg-gray-100 scrolling-touch overscroll-none">
                <TournamentHeader
                  activeTab={activeTab}
                  selectedTournament={selectedTournament}
                  courseName={currentCourse?.name || 'No Course Selected'}
                  year={selectedYear}
                  onYearChange={setSelectedYear}
                  setActiveTab={setActiveTab}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  date={selectedTournament?.startDate || formattedDate}
                  isAdmin={isAdmin}
                  isConnected={isConnected && !isOffline}
                  onAdminClick={() => setShowAdminPrompt(true)}
                  onLogout={handleAdminLogout}
                  onPasswordChange={handlePasswordChange}
                  onDateChange={(startDate, endDate) => {
                    if (!selectedTournament) return;
                    handleTournamentUpdate({
                      ...selectedTournament,
                      startDate,
                      endDate
                    });
                  }}
                  onTitleChange={(title) => {
                    if (!selectedTournament) return;
                    handleTournamentUpdate({
                      ...selectedTournament,
                      name: title
                    });
                  }}
                  courseInfo={currentCourse ? {
                    name: currentCourse.name,
                    location: currentCourse.location,
                    selectedTee: currentCourse.tees.find(t => t.name === selectedTournament?.selectedTee)
                  } : undefined}
                />

            {/* Admin Password Prompt */}
            {/* Registration Modal */}
            {isRegistering && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
                <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
                  <RegistrationModule
                    onComplete={async (memberId, isNewMember, formData) => {
                      completeRegistration();
                      if (!players.some(p => p.id === memberId)) {
                        const { data: member } = await supabase
                          .from('members')
                          .select('*')
                          .eq('id', memberId)
                          .single();

                        if (member) {
                          const newPlayer: Player = {
                            id: member.id,
                            name: member.name,
                            tournamentId: selectedTournament?.id,
                            handicap: member.season?.['2025']?.handicap || 0,
                            flight: member.season?.['2025']?.flight || 'A',
                            //notes: formData.notes,
                            scores: [],
                            totalScore: 0
                          };
                          handlePlayersUpdate([...players, newPlayer]);
                        }
                      }
                    }}
                    onCancel={cancelRegistration}
                  />
                </div>
              </div>
            )}
            {showAdminPrompt && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
                  <h3 className="text-lg font-semibold mb-4">Admin Access</h3>
                  <form onSubmit={handleAdminLogin} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Password
                      </label>
                      <input
                        type="password"
                        value={adminPasswordInput}
                        onChange={(e) => setAdminPasswordInput(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                        autoFocus
                      />
                    </div>
                    {adminError && (
                      <p className="text-sm text-red-600">{adminError}</p>
                    )}
                    <div className="flex justify-end gap-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAdminPrompt(false);
                          setAdminPasswordInput('');
                          setAdminError('');
                        }}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                      >
                        Login
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-24 sm:pb-8">
              {/* Version Check Component */}
              <VersionCheck buildTime={BUILD_TIME} buildVersion={BUILD_VERSION} />
              
              {/* Only show tournament-related tabs when not in members view */}
              {activeTab !== 'members' && (
                <div className={`mb-6 relative ${!isAdmin && activeTab === 'tournaments' ? 'hidden' : ''}`}>
                  <Navigation 
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    isAdmin={isAdmin}
                    selectedTournament={selectedTournament}
                    isConnected={isConnected}
                    players={players.filter(p => p.tournamentId === selectedTournament?.id)}
                    tournaments={tournaments}
                    onTournamentSelect={(tournament) => {
                      setSelectedTournament(tournament);
                      if (isAdmin && tournament.status === 'draft') {
                        // If admin and draft status, go to setup
                        setActiveTab('import');
                      } else if (tournament.status === 'active') {
                        // For active tournaments, go to scoring
                        setActiveTab(tournament.format === 'match' ? 'matchplay' : 'scoring');
                      } else if (tournament.status === 'registration') {
                        // For registration tournaments, show registration tab
                        setActiveTab('registration');
                      } else {
                        // For completed tournaments, show leaderboard
                        setActiveTab('leaderboard');
                      }
                    }}
                  />
                </div>
              )}

              <MainContent
                activeTab={activeTab}
                isAdmin={isAdmin}
                tournaments={tournaments}
                players={players}
                selectedTournament={selectedTournament}
                onTournamentSelect={(tournament) => {
                  setSelectedTournament(tournament);
                  if (isAdmin && tournament.status === 'draft') {
                    // If admin and draft status, go to setup
                    setActiveTab('import');
                  } else if (tournament.status === 'active') {
                    // For active tournaments, go to scoring
                    setActiveTab(tournament.format === 'match' ? 'matchplay' : 'scoring');
                  } else if (tournament.status === 'registration') {
                    // For registration tournaments, show registration tab
                    setActiveTab('registration');
                  } else {
                    // For completed tournaments, show leaderboard
                    setActiveTab('leaderboard');
                  }
                }}
                onCreateTournament={handleCreateTournament}
                onDeleteTournament={handleDeleteTournament}
                onTournamentUpdate={handleTournamentUpdate}
                onPlayersImported={handlePlayersUpdate}
                currentCourse={currentCourse}
                selectedPlayers={selectedPlayers}
                setSelectedPlayers={setSelectedPlayers}
                scores={scores}
                setScores={setScores}
                isSelectingPlayers={isSelectingPlayers}
                setIsSelectingPlayers={setIsSelectingPlayers}
                currentHoleIndex={currentHoleIndex}
                setCurrentHoleIndex={setCurrentHoleIndex}
                onScoreSubmit={handleScoreSubmit}
                onResetScoring={handleResetScoring}
                completedHoles={completedHoles}
                setCompletedHoles={setCompletedHoles}
                matches={matches}
                onMatchesUpdate={handleMatchesUpdate}
                activeMatch={activeMatch}
                year={selectedYear}
                setActiveMatch={setActiveMatch}
                membersData={membersData}
                setMembersData={setMembersData}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </main>

                {/* Chat Overlay */}
                {selectedTournament && activeTab !== 'members' && (
                  <ChatOverlay
                    tournamentId={selectedTournament.id}
                    isAdmin={isAdmin}
                    show={true}
                  />
                )}
                <OGChatbot />
              </div>
                </SponsorProvider>
              </PlayerDeletionProvider>
              </PlayerLockProvider>
              )}
            </RegistrationProvider>
          </TXCupProvider>
        </ChatProvider>
      </StorageProvider>
    </>
  );
}